import { type ReactNode } from "react";

import { BTText } from "@btnet/core-components";
import type { CustomFieldAssociatedType } from "@btnet/services-btnet/handlers/models/customFieldAssociatedType";
import type { ListOfUsersCustomFieldDefinition } from "@btnet/services-btnet/handlers/models/listOfUsersCustomFieldDefinition";
import type { ListOfUsersCustomFieldValue } from "@btnet/services-btnet/handlers/models/listOfUsersCustomFieldValue";

import { friendlyDefaultDisplayValue } from "utilities/helpers";

import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import {
    getOptionsFromDefinition,
    getOptionText,
} from "commonComponents/entity/customField/utils/customFieldTypes/selectCustomFieldUtils";
import { UserSubCellEditor } from "commonComponents/entity/customField/utils/customFieldTypes/UserSubCellEditor";
import {
    defaultRenderAgGridCell,
    type ICustomFieldTypeHandler,
} from "commonComponents/entity/customField/utils/customFieldValues.util";

export const ListOfUsersCustomFieldHandler: ICustomFieldTypeHandler<
    string,
    ListOfUsersCustomFieldValue,
    ListOfUsersCustomFieldDefinition
> = {
    getValue(field): string | null {
        return field.selectedUserId != null && field.selectedUserId > 0
            ? String(field.selectedUserId)
            : "";
    },

    setValue(field, value): ListOfUsersCustomFieldValue {
        const parsed = value ? parseInt(value, 10) : -1;
        return {
            ...field,
            selectedUserId: isNaN(parsed) ? -1 : parsed,
        };
    },

    createValue(
        customFieldId: number,
        associatedType: CustomFieldAssociatedType,
        associatedTypeId: number,
        value: string | null,
    ): ListOfUsersCustomFieldValue {
        return {
            dataType: "ListOfUsers",
            customFieldId,
            customFieldValueId: 0,
            associatedType,
            associatedTypeId,
            selectedUserId: value ? parseInt(value, 10) : -1,
        };
    },

    compare(valueA: string | null, valueB: string | null, definition?): number {
        const textA = valueA ? getOptionText(parseInt(valueA, 10), definition) : "";
        const textB = valueB ? getOptionText(parseInt(valueB, 10), definition) : "";
        return textA.localeCompare(textB, undefined, { sensitivity: "base" });
    },

    renderDisplay(value: string | null, definition): ReactNode {
        if (!value) {
            return <BTText size="normal-md" text={friendlyDefaultDisplayValue} />;
        }
        const userId = parseInt(value, 10);
        if (isNaN(userId)) {
            return <BTText size="normal-md" text={friendlyDefaultDisplayValue} />;
        }
        const displayText = getOptionText(userId, definition);
        return <BTText size="normal-md" text={displayText} />;
    },

    renderEditor(
        value: string | null,
        customFieldId: number,
        onChange: (newValue: string | null) => void,
        onBlur: () => void,
        _autoFocus?: boolean,
        definition?: ListOfUsersCustomFieldDefinition,
        _field?: ListOfUsersCustomFieldValue,
        formMode?: "always-editing" | "click-to-edit",
    ): ReactNode {
        const treeData = getOptionsFromDefinition(definition);
        const parsedValue = value ? parseInt(value, 10) : null;
        const selectedValues =
            parsedValue != null && !isNaN(parsedValue) ? [parsedValue] : undefined;

        return (
            <BTSelect
                id={`listOfUsersCustomFieldEditor-${customFieldId}`}
                data-testid="listOfUsersCustomFieldEditor"
                value={selectedValues}
                treeData={treeData}
                onChange={(_id, val) => {
                    const selected = val as number;
                    onChange(selected != null ? String(selected) : null);
                }}
                onBlur={() => onBlur()}
                multiple={false}
                formMode={formMode ?? "click-to-edit"}
                placeholder={friendlyDefaultDisplayValue}
            />
        );
    },

    getAgGridCellEditor() {
        return UserSubCellEditor;
    },

    renderAgGridCell(context): ReactNode {
        return defaultRenderAgGridCell(context);
    },
};
